import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, FileText, Save, Eye, Trash2 } from "lucide-react";
import { api, getAdminToken } from "@/lib/api";

const auth = () => ({ headers: { Authorization: `Bearer ${getAdminToken()}` } });

const downloadPdf = async (c) => {
  try {
    const r = await api.get(`/admin/carnets/${c.id}/pdf`, { ...auth(), responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = c.pdf_filename || `carnet-${c.number || c.id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch { toast.error("Téléchargement impossible"); }
};

export const BookPdfPanel = ({ book, carnet, onChange, onBookChange }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm({
      price: ((carnet?.price_cents ?? 990) / 100).toFixed(2).replace(".", ","),
      active_sale: carnet?.active_sale ?? true,
      visible: carnet?.visible ?? true,
    });
  }, [carnet]);

  const payload = () => ({
    number: book.number, name: book.title, title: book.title,
    price_cents: Math.round(parseFloat(String(form.price).replace(",", ".")) * 100) || 990,
    active_sale: form.active_sale, visible: form.visible,
  });
  const ensure = async () => carnet || (await api.post("/admin/carnets", { ...payload(), book_id: book.id }, auth())).data;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const c = await ensure();
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/carnets/${c.id}/pdf`, fd, { headers: { ...auth().headers, "Content-Type": "multipart/form-data" } });
      toast.success("PDF chargé"); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    setBusy(false);
  };
  const save = async () => {
    try {
      if (carnet) await api.put(`/admin/carnets/${carnet.id}`, payload(), auth());
      else await ensure();
      toast.success("Réglages PDF enregistrés"); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
  };

  const uploadExcerpt = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/admin/books/${book.id}/excerpt`, fd, { headers: { ...auth().headers, "Content-Type": "multipart/form-data" } });
      toast.success("Extrait chargé"); onBookChange(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
  };
  const removeExcerpt = async () => {
    if (!window.confirm("Retirer l'extrait ?")) return;
    const r = await api.delete(`/admin/books/${book.id}/excerpt`, auth());
    toast.success("Extrait retiré"); onBookChange(r.data);
  };

  if (!form) return null;
  const id = `book-pdf-${book.id}`;
  return (
    <div className="w-full rounded-lg bg-[#F8F5EE] border border-[#E8DCC4] p-3 flex flex-wrap items-center gap-3" data-testid={`book-pdf-panel-${book.number}`}>
      <span className="inline-flex items-center gap-1.5 font-sans text-xs text-[#1F2A24]/70 min-w-[150px]" data-testid={`book-pdf-status-${book.number}`}>
        <FileText size={14} className={carnet?.has_pdf ? "text-[#385E3A]" : "text-[#A63B36]"} />
        {carnet?.has_pdf ? carnet.pdf_filename || "PDF chargé" : "Aucun PDF"}
      </span>
      <input type="file" accept="application/pdf" id={id} className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid={`book-pdf-file-${book.number}`} />
      <label htmlFor={id} data-testid={`book-pdf-upload-${book.number}`} className="px-3 py-1.5 rounded-full border border-[#385E3A] text-[#385E3A] text-xs inline-flex items-center gap-1 cursor-pointer hover:bg-[#385E3A]/10">
        <Upload size={12} /> {busy ? "Envoi…" : carnet?.has_pdf ? "Remplacer le PDF" : "Envoyer le PDF"}
      </label>
      {carnet?.has_pdf && (
        <button onClick={() => downloadPdf(carnet)} data-testid={`book-pdf-download-${book.number}`} className="px-3 py-1.5 rounded-full border text-xs inline-flex items-center gap-1">
          <Download size={12} /> Télécharger
        </button>
      )}
      <label className="font-sans text-xs text-[#1F2A24]/70 inline-flex items-center gap-1">
        Prix PDF
        <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-16 border rounded px-2 py-1 text-xs" data-testid={`book-pdf-price-${book.number}`} /> €
      </label>
      <label className="font-sans text-xs text-[#1F2A24]/70 inline-flex items-center gap-1">
        <input type="checkbox" checked={form.active_sale} onChange={(e) => setForm({ ...form, active_sale: e.target.checked })} data-testid={`book-pdf-sale-${book.number}`} /> En vente
      </label>
      <label className="font-sans text-xs text-[#1F2A24]/70 inline-flex items-center gap-1">
        <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid={`book-pdf-visible-${book.number}`} /> Visible en boutique
      </label>
      <button onClick={save} data-testid={`book-pdf-save-${book.number}`} className="p-1.5 rounded-full bg-[#385E3A] text-white" title="Enregistrer"><Save size={13} /></button>
      {book.type === "foundation" && (
        <div className="w-full flex flex-wrap items-center gap-3 pt-2 border-t border-[#E8DCC4]" data-testid="book-excerpt-slot">
          <span className="inline-flex items-center gap-1.5 font-sans text-xs text-[#1F2A24]/70 min-w-[150px]" data-testid="book-excerpt-status">
            <Eye size={14} className={book.excerpt_path ? "text-[#385E3A]" : "text-[#1F2A24]/40"} />
            Extrait gratuit : {book.excerpt_path ? book.excerpt_filename || "chargé" : "aucun"}
          </span>
          <input type="file" accept="application/pdf" id={`excerpt-${book.id}`} className="hidden" onChange={(e) => { uploadExcerpt(e.target.files[0]); e.target.value = ""; }} data-testid="book-excerpt-file" />
          <label htmlFor={`excerpt-${book.id}`} data-testid="book-excerpt-upload" className="px-3 py-1.5 rounded-full border border-[#385E3A] text-[#385E3A] text-xs inline-flex items-center gap-1 cursor-pointer hover:bg-[#385E3A]/10">
            <Upload size={12} /> {book.excerpt_path ? "Remplacer l'extrait" : "Envoyer l'extrait (PDF)"}
          </label>
          {book.excerpt_path && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/books/${book.id}/excerpt`} target="_blank" rel="noreferrer" className="text-xs text-[#385E3A] underline" data-testid="book-excerpt-view">Voir</a>}
          {book.excerpt_path && <button onClick={removeExcerpt} data-testid="book-excerpt-remove" className="p-1.5 rounded border text-[#A63B36]"><Trash2 size={12} /></button>}
        </div>
      )}
    </div>
  );
};
